/* The support card — v3.3 migration, pack supports-a (WF-014 list · WF-006 the campaign's Supports tab).
   One card per support: its thumbnail, its model word, its place and access method, what it opens today (« En ligne »),
   its readiness (lib/readiness: five states, a to-do pill and one line of guidance — never a gate), its figures, and
   « Ouvrir ». « Faire pointer vers… » and « Tester ce QR » stay on the card: the same controls as before, one verb.
   Grouping: by emplacement (« Emplacement à préciser » when none), by campaign, or none. */
import type { ReactNode } from 'react'
import { Button, Icon, List, ListRow, Pill, type PillTone } from '@/ui'
import { supportUrl } from '@/lib/api'
import { defaultSupportDesign, servedBy, servedSentence, supportNumbers, SUPPORT_MODELS, type CampaignLite, type SupportRef } from '@/lib/campaign'
import { readiness, type ReadinessState } from '@/lib/readiness'
import { getMembers } from '@/lib/team'
import { SupportPreview } from './Designer'
import { modelOf } from './Imprimer'
import { PointerSelect, TesterQR } from './Pointer'
import { EtiquettesChips } from '@/routes/campagnes/EtiquettesPicker'
import { supportPlace } from '@/lib/organisation'

export const PLACE_TBD = 'Emplacement à préciser'
/* point 6 · the emplacement is the list's (lib/organisation) once the support carries one; else the free text (before 0039,
   or a support from before the list) */
export const placeOf = (s: SupportRef): string => (supportPlace(s.id, s as { placeId?: string | null })?.name ?? s.place)?.trim() || PLACE_TBD
export const isPersonal = (s: SupportRef) => s.kind === 'personnel' || !!s.memberId

/** The model word of a support: its designed model, else what its kind says. */
export function modelLabel(s: SupportRef): string {
  if (isPersonal(s)) return 'Support d’équipe'
  const m = modelOf(s)
  if (m) return SUPPORT_MODELS.find(x => x.id === m)?.label ?? s.kind
  if (s.methods.includes('nfc')) return 'Carte NFC + QR'
  if (s.kind === 'lien' || s.kind === 'web' || s.methods.includes('web')) return 'Lien ou QR seul'
  if (s.kind === 'écran') return 'Écran à scanner'
  return s.kind
}
/** « QR », « QR + NFC », « LIEN » — the ways in, as the card prints them. */
export const methodsLabel = (s: SupportRef) => s.methods.map(m => m === 'web' ? 'LIEN' : m.toUpperCase()).join(' + ')
/** The format family a filter reads: print · nfc · ecran · lien. */
export function formatOf(s: SupportRef): 'print' | 'nfc' | 'ecran' | 'lien' {
  if (s.methods.includes('nfc')) return 'nfc'
  if (s.design?.model === 'ecran-portrait' || s.kind === 'écran') return 'ecran'
  if (s.design || ['chevalet', 'sticker', 'affiche', 'grand format'].includes(s.kind)) return 'print'
  return 'lien'
}

/** The readiness of a support, summed up: the next to-do (or « Prêt pour les clients »), as a pill word and a line. */
export function readinessOf(s: SupportRef, list: CampaignLite[]): { states: ReadinessState[]; next: ReadinessState | null; ready: boolean; tone: PillTone; word: string; line: string } {
  const r = servedBy(s, list)
  const states = readiness(s, r.current)
  const next = states.find(x => !x.done) ?? null
  if (!next) return { states, next, ready: true, tone: 'ok', word: 'Prêt pour les clients', line: 'Les cinq états sont là. Un changement de campagne redemande la vérification de l’accès.' }
  /* a destination that is paused, ended or missing reads first, in the sentence's words (rule 15) */
  const tone: PillTone = next.key === 'destination' ? 'crit' : 'warn'
  return { states, next, ready: false, tone, word: next.label, line: next.hint }
}

/** The thumbnail: the visual at a small width, or the icon of what the support is. */
function Thumb({ s, place }: { s: SupportRef; place: string }) {
  /* the visual, or the standard model's sheet for a support that has none yet (the same sheet its print file draws) */
  const m = isPersonal(s) ? null : modelOf(s)
  const design = s.design ?? (m ? defaultSupportDesign(m) : undefined)
  if (design) return <div style={{ display: 'grid', placeItems: 'center', padding: 10 }}><SupportPreview design={design} place={place} width={design.model === 'ecran-portrait' ? 54 : 84} url={s.token ? supportUrl(s.token) : undefined} /></div>
  return <div style={{ display: 'grid', placeItems: 'center', height: 104, color: 'var(--bmr-action)' }}><Icon name={isPersonal(s) ? 'user' : s.methods.includes('nfc') ? 'nfc' : formatOf(s) === 'ecran' ? 'screen' : formatOf(s) === 'lien' ? 'link' : 'qr'} size={30} /></div>
}

export function SupportCard({ s, list, names, showLocation, highlight, onOpen, onChanged, end }:
  { s: SupportRef; list: CampaignLite[]; names: Record<string, string>; showLocation?: boolean; highlight?: string; onOpen: (id: string) => void; onChanged: () => void; end?: ReactNode }) {
  const placeName = names[s.locationId] ?? 'l’établissement'
  const r = servedBy(s, list)
  const n = supportNumbers(s)
  const rd = readinessOf(s, list)
  const member = s.memberId ? getMembers().find(m => m.id === s.memberId) : undefined
  return (
    <article className="card" data-testid="support-card" data-support={s.id} style={{ padding: 0, overflow: 'hidden', display: 'grid', gridTemplateRows: 'auto 1fr' }}>
      <div style={{ background: 'var(--bmr-surface-sunken)', borderBottom: '1px solid var(--bmr-border)' }}><Thumb s={s} place={placeName} /></div>
      <div style={{ padding: 14, display: 'grid', gap: 10, alignContent: 'start' }}>
        <div style={{ display: 'grid', gap: 4 }}>
          <span><Pill tone="ink">{modelLabel(s)}</Pill></span>
          <p className="t-section" style={{ margin: '2px 0 0' }}>{member ? `${member.firstName} · support personnel` : s.name}</p>
          <p className="f-help" style={{ margin: 0 }}>{placeOf(s)} · {methodsLabel(s)}{showLocation ? ` · ${placeName}` : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid var(--bmr-border)' }}>
          {r.current
            ? <><Pill tone="ok">En ligne</Pill><span className="t-body" style={{ fontSize: 13 }}>{r.current.name}</span>{highlight && <Pill tone={r.current.id === highlight ? 'ok' : 'done'}>{r.current.id === highlight ? 'pointe ici' : 'ailleurs'}</Pill>}</>
            : <><Pill tone="warn" mark="⊘">Hors ligne</Pill><span className="f-help">{servedSentence(s, list, placeName)}</span></>}
          {s.silentDays !== null && <Pill tone="warn">Silencieux</Pill>}
          {s.pinned && <Pill tone="done">fixe</Pill>}
        </div>
        {/* « Étiquettes » (ruling « Organisation »): the support's own markers, never a customer's sight */}
        <EtiquettesChips kind="support" id={s.id} />
        <div style={{ display: 'grid', gap: 4, paddingTop: 8, borderTop: '1px solid var(--bmr-border)' }} data-testid="support-readiness">
          <span><Pill tone={rd.tone}>{rd.word}</Pill></span>
          <p className="f-help" style={{ margin: 0 }}>{rd.line}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid var(--bmr-border)' }}>
          <PointerSelect support={s} list={list} placeName={placeName} highlight={highlight} onChanged={onChanged} />
          <TesterQR support={s} list={list} />
          {end}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'end', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--bmr-border)' }}>
          <div>
            <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600 }}>{s.silentDays !== null ? `aucun scan depuis ${s.silentDays} jours` : `${n.scans30} scans / 30 j · ${n.participations} participations`}</p>
            <p className="f-help" style={{ margin: 0 }}>{s.silentDays !== null ? 'déplacé ? tombé ?' : `30 jours · tests exclus${n.lastScan ? ` · dernier scan ${n.lastScan}` : ''}`}</p>
          </div>
          <Button size="sm" onClick={() => onOpen(s.id)}>Ouvrir</Button>
        </div>
      </div>
    </article>
  )
}

export type Grouping = 'emplacement' | 'campagne' | 'aucun'
/** The sections of a list of supports, by the grouping chosen. */
export function groupSupports(list: SupportRef[], by: Grouping, lite: CampaignLite[]): { key: string; title: string; rows: SupportRef[] }[] {
  if (by === 'aucun') return list.length ? [{ key: 'all', title: '', rows: list }] : []
  const map = new Map<string, SupportRef[]>()
  for (const s of list) { const k = by === 'emplacement' ? placeOf(s) : (servedBy(s, lite).current?.name ?? 'Sans campagne en ligne'); map.set(k, [...(map.get(k) ?? []), s]) }
  const keys = [...map.keys()].sort((a, b) => (a === PLACE_TBD || a === 'Sans campagne en ligne' ? 1 : 0) - (b === PLACE_TBD || b === 'Sans campagne en ligne' ? 1 : 0) || a.localeCompare(b, 'fr'))
  return keys.map(k => ({ key: k, title: `${k} · ${map.get(k)!.length}`, rows: map.get(k)! }))
}

export const foundLine = (n: number) => `${n} support${n > 1 ? 's' : ''} trouvé${n > 1 ? 's' : ''}`

export function CardGrid({ children }: { children: ReactNode }) {
  return <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 250px), 1fr))' }}>{children}</div>
}

/* ---------- WF-006 · the campaign's Supports tab: the same cards, grouped by emplacement, under the campaign's words ---------- */
export function CampaignSupports({ campaignId, campaignName, covered, names, lite, supports, personal, members, onCreate, onOpen, onChanged, onMemberQr, memberLink }:
  { campaignId: string; campaignName: string; covered: string[]; names: Record<string, string>; lite: CampaignLite[]; supports: SupportRef[]; personal: SupportRef[]
    members: { id: string; firstName: string }[]; onCreate: () => void; onOpen: (id: string) => void; onChanged: () => void; onMemberQr: () => void; memberLink: ReactNode }) {
  const all = [...supports, ...personal]
  const sections = groupSupports(all, 'emplacement', lite)
  const without = members.filter(m => !personal.some(s => s.memberId === m.id))
  const where = covered.length > 1 ? 'dans vos établissements' : `à ${names[covered[0]] ?? 'l’établissement'}`
  return (
    <section className="sect" data-testid="onglet-supports" style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px' }}>
          <p className="t-section" style={{ margin: 0 }}>La mettre en place {where}</p>
          <p className="f-help" style={{ margin: '4px 0 0' }}>Un support par emplacement. La roue, les demandes et les cadeaux restent dans la campagne. Un support suit <b>la campagne principale de l’établissement</b> (à défaut, la première en ligne) ou pointe vers <b>une campagne précise</b>&nbsp;: «&nbsp;Faire pointer vers…&nbsp;» est le seul réglage, et changer ne réimprime jamais.</p>
        </div>
        <Button size="sm" variant="primary" onClick={onCreate}><Icon name="plus" /> Ajouter un support à cette campagne</Button>
      </div>
      {all.length === 0
        ? <p className="f-help" style={{ margin: 0 }}>Aucun support — sans support posé, rien ne se passe. «&nbsp;Ajouter un support à cette campagne&nbsp;» crée le premier.</p>
        : <>
          <p className="f-help" style={{ margin: 0 }} data-testid="supports-trouves">{foundLine(all.length)}</p>
          {sections.map(sec => (
            <div key={sec.key} style={{ display: 'grid', gap: 8 }}>
              <p className="t-eyebrow" style={{ margin: 0 }}>{sec.title}</p>
              <CardGrid>{sec.rows.map(s => <SupportCard key={s.id} s={s} list={lite} names={names} showLocation={covered.length > 1} highlight={campaignId} onOpen={onOpen} onChanged={onChanged} />)}</CardGrid>
            </div>
          ))}
        </>}
      <p className="t-eyebrow" style={{ margin: '8px 0 0' }}>Votre équipe <span className="f-help">· un QR par personne — chaque participation lui est attribuée{campaignName ? `, quelle que soit la campagne ouverte` : ''}</span></p>
      {members.length === 0 ? <p className="f-help" style={{ margin: 0 }}>Pas encore de membre — {memberLink}.</p>
        : without.length === 0 ? <p className="f-help" style={{ margin: 0 }}>Chaque membre a son QR personnel — ci-dessus, parmi les supports.</p>
        : <List>{without.map(m => <ListRow key={m.id} icon={<Icon name="team" size={18} />} title={m.firstName} meta="pas encore de QR personnel" end={<Button size="sm" variant="quiet" onClick={onMemberQr}>Créer son QR</Button>} />)}</List>}
    </section>
  )
}
